// A notes log: write one, and it is stamped with who wrote it and when.
//
// Shared by clients and jobs (round 34), because they are the same feature
// twice and two copies would drift the way Zanetto Tools' two copies did.
//
// APPEND-ONLY, and that is the whole point. The thing this replaced was a
// single shared textarea, which is a whiteboard: two people edit it in the
// same hour and one of them silently loses what the other wrote, and nothing
// on it says who said what. A note that can be edited afterwards is that
// whiteboard again with extra steps, so there is no edit control here and no
// route behind one — a correction is another note, which is the rule the
// service records and meter readings already run under.
import { useState } from 'react';
import { ApiError } from '../api';

export interface Note {
  id: string;
  body: string;
  createdByName: string;
  createdAt: string;
}

/**
 * "Mick Harlan · 8 Sep 2026, 3:04 pm".
 *
 * `toLocaleString('en-AU')` is what every other screen uses for a timestamp.
 * Round 45: DD/MM/YYYY like every other date (Nathan: "1 Nov" style dates
 * hid the year and looked unprofessional), '08/09/2026, 3:04 pm'.
 */
function when(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function NotesPanel({
  title = 'Notes',
  notes,
  canWrite,
  intro,
  emptyText = 'No notes yet.',
  onAdd,
}: {
  title?: string;
  notes: Note[];
  /** Managers and admins write; a read-only viewer still sees the log. */
  canWrite: boolean;
  /** One line saying who these are for, when that is not obvious. */
  intro?: string;
  emptyText?: string;
  onAdd: (body: string) => Promise<void>;
}) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (body.trim() === '') return;
    setBusy(true);
    setError(null);
    try {
      await onAdd(body.trim());
      setBody('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that note.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <h2>{title}</h2>
      {intro && <p className="muted">{intro}</p>}

      {canWrite && (
        <div className="mb-4">
          {error && (
            <p className="field-error-inline" role="alert">
              {error}
            </p>
          )}
          <label htmlFor="note-body" className="sr-only">
            Add a note
          </label>
          <textarea
            id="note-body"
            rows={3}
            value={body}
            placeholder="Add a note…"
            disabled={busy}
            onChange={(event) => setBody(event.target.value)}
          />
          <div className="mt-2 flex items-center justify-between gap-3">
            <span className="hint">
              Notes are permanent and show who wrote them. Correct one by adding another.
            </span>
            <button
              type="button"
              className="btn-primary btn-sm flex-none"
              disabled={busy || body.trim() === ''}
              onClick={() => void save()}
            >
              {busy ? 'Saving…' : 'Save note'}
            </button>
          </div>
        </div>
      )}

      {notes.length === 0 ? (
        <p className="muted">{emptyText}</p>
      ) : (
        <ul className="m-0 list-none space-y-3 p-0">
          {notes.map((note) => (
            <li
              key={note.id}
              className="border-t border-gray-200 pt-3 first:border-0 first:pt-0"
            >
              <div className="text-xs text-gray-500">
                <span className="font-medium text-gray-700">{note.createdByName}</span>
                <span className="mx-1.5">·</span>
                {when(note.createdAt)}
              </div>
              {/* whitespace-pre-wrap: somebody typing a list in a note gets
                  the list back, rather than one run-on paragraph. */}
              <div className="mt-0.5 whitespace-pre-wrap text-sm text-gray-800">{note.body}</div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
